import ItiDataGrid from "@/iti-core-react/components/ItiDataGrid/ItiDataGrid";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import { getRecyclingGlossaryColumns } from "@/pages/Glossary/GlossaryManagement/GlossaryColumns";
import { GlossaryV2Dto } from "@/shared/dtos/glossary-v2-dto";
import { useRef, useState } from "react";
import GlossaryDetailsPopUp from "./GlossaryDetailsPopUp/GlossaryDetailsPopUp";
import { useRecycleGlossary } from "./useRecycleGlossary";

export default function RecycleGlossaryList() {
	const { t } = useLanguage();
	const [showDetailsPopup, setShowDetailsPopup] = useState<boolean>(false);
	const [selectedGlossary, setSelectedGlossary] =
		useState<GlossaryV2Dto | null>(null);

	const gridRef = useRef<HTMLDivElement>(null);

	const {
		glossaries,
		pageIndex,
		pageSize,
		handlePageIndexChanged,
		handlePageSizeChanged,
		restoreGlossary,
		permanentDeleteGlossary,
	} = useRecycleGlossary();

	const baseColumns = getRecyclingGlossaryColumns(t);

	const onViewDetails = async (e: any) => {
		const glossary = e.row.data;
		setSelectedGlossary(new GlossaryV2Dto(glossary));
		setShowDetailsPopup(true);
	};

	const onRestoreGlossary = async (e: any) => {
		const glossary = e.row.data;
		try {
			const success = await restoreGlossary(glossary.id);
			if (success) {
				ItiMessageService.toastSuccess(
					t("recycle.glossary-restored-successfully", {
						name: glossary.title,
					}),
				);
			}
		} catch (error) {
			ItiMessageService.toastError(t("recycle.error-restoring-glossary"));
		}
	};

	const onPermanentDelete = async (e: any) => {
		const glossary = e.row.data;
		ItiMessageService.confirmYesNoButtons(
			t("recycle.confirm-permanent-delete", {
				name: glossary.title || glossary.name,
			}),
		).then(async (confirmed: boolean) => {
			if (confirmed) {
				try {
					const success = await permanentDeleteGlossary(glossary.id);
					if (success) {
						ItiMessageService.toastSuccess(
							t("recycle.glossary-deleted-permanently", {
								name: glossary.title || glossary.name,
							}),
						);
					}
				} catch (error) {
					ItiMessageService.toastError(
						t("recycle.error-deleting-glossary-permanently"),
					);
				}
			}
		});
	};

	const rowButtons = [
		{
			hint: t("recycle.view-details"),
			icon: "mdi mdi-eye",
			name: "view-details",
			visible: true,
			onClick: onViewDetails,
		},
		{
			hint: t("recycle.restore"),
			icon: "mdi mdi-delete-restore",
			name: "restore",
			visible: true,
			onClick: onRestoreGlossary,
		},
		{
			hint: t("recycle.permanent-delete"),
			icon: "mdi mdi-delete-forever",
			name: "permanent-delete",
			visible: true,
			onClick: onPermanentDelete,
		},
	];

	const glossaryColumns = [
		...baseColumns,
		{
			caption: t("actions"),
			type: "buttons",
			buttons: rowButtons,
			fixed: true,
			fixedPosition: "right",
		},
	];

	return (
		<>
			<div ref={gridRef} className="position-relative">
				<ItiDataGrid
					keyExpr="id"
					entity={"deletedGlossaries"}
					elementAttr={{ class: "basic-grid-toolbar-white" }}
					dataSource={glossaries}
					columns={glossaryColumns}
					rowButtons={rowButtons}
					allowDeleting={false}
					allowUpdating={false}
					allowAdding={false}
					pagingEnabled={true}
					remoteOperations={false}
					showPageSizeSelector={true}
					pageSize={pageSize}
					pageIndex={pageIndex}
					onPageIndexChanged={handlePageIndexChanged}
					onPageSizeChanged={handlePageSizeChanged}
				/>
			</div>

			{/* Popup de detalles */}
			{showDetailsPopup && selectedGlossary && (
				<GlossaryDetailsPopUp
					glossary={selectedGlossary}
					onHide={() => {
						setShowDetailsPopup(false);
						setSelectedGlossary(null);
					}}
				/>
			)}
		</>
	);
}
